import { STORAGE_BASE_URL } from './constant';

export type StorageAssetType =
  | 'prompt'
  | 'moh'
  | 'tts'
  | 'recordings'
  | 'voicemails'
  | 'reports'
  | 'logo'
  | 'voice_base'
  | 'numbers'
  | 'agents'
  | 'contacts';

/**
 * The gateway URL for a stored blob.
 *
 *   https://{domain}/storage/{container}/{blob path}
 *
 * The container-qualified blob path IS the URL - nothing is prepended. There is
 * no asset type in the path any more: the gateway derives the storage account
 * and the cache policy from the container and the folder inside it, so the
 * caller never names an account and the account is never exposed.
 *
 * `type` is still accepted so the dozen call sites did not all have to change,
 * but it no longer reaches the URL. It stays useful as a readable label of what
 * the caller thinks it is fetching.
 *
 * @param type     what the caller is fetching - not part of the URL
 * @param blobPath container-qualified path from the API,
 *                 e.g. "cp-20-assets/logo/a.png"
 * @returns the URL, or null when there is no blob path to point at
 */
export function buildStorageUrl(
  type: StorageAssetType,
  blobPath?: string | null,
): string | null {
  const trimmed = String(blobPath || '').trim();

  if (!trimmed) return null;

  const base = String(STORAGE_BASE_URL || '').replace(/\/+$/, '');

  let segments = trimmed.replace(/^\/+/, '').split('/').filter(Boolean);
  const [, ownerRoot] = segments;

  if (ownerRoot && /^(?:cp-\d+-(?:prompts|assets)|sa-assets)$/.test(ownerRoot)) {
    segments = segments.slice(1);
  }

  const encoded = segments.map(encodeURIComponent).join('/');

  if (!encoded) return null;

  void type;

  return `${base}/${encoded}`;
}

export function storageUrlOr(
  type: StorageAssetType,
  blobPath: string | null | undefined,
  fallbackUrl: string,
): string {
  return buildStorageUrl(type, blobPath) || fallbackUrl;
}
